import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../shared/toastr.service";
import {
  GET_EMPLOYEE_SETTINGS,
  GetEmployeeSettingsAction,
  GetEmployeeSettingsSuccessAction,
  GetEmployeeSettingsFailureAction,
  CHANGE_EMPLOYEE_SETTINGS,
  CHANGE_EMPLOYEE_SETTINGS_FAILURE,
  ChangeEmployeeSettingsAction,
  ChangeEmployeeSettingsSuccessAction,
  ChangeEmployeeSettingsFailureAction,
  ChangeCompanySettingsAction,
  CHANGE_COMPANY_SETTINGS,
  ChangeCompanySettingsSuccessAction,
  ChangeCompanySettingsFailureAction,
  GET_COMPANY_SETTINGS,
  GetCompanySettingsSuccessAction,
  GetCompanySettingsFailureAction,
  GET_PREDEFINED_EMPLOYEE_SETTINGS,
  GetPredefinedEmployeeSettingsSuccessAction,
  GetPredefinedEmployeeSettingsFailureAction,
  GET_ADVANCED_SETTINGS,
  GetAdvancedSettingsSuccessAction,
  GetAdvancedSettingsFailureAction,
  CHANGE_ADVANCED_SETTINGS,
  CHANGE_ADVANCED_SETTINGS_FAILURE,
  ChangeAdvancedSettingsAction,
  ChangeAdvancedSettingsSuccessAction,
  ChangeAdvancedSettingsFailureAction,
  GET_BILLING_SETTINGS,
  GetBillingSettingsSuccessAction,
  GetBillingSettingsFailureAction,
  CHANGE_BILLING_SETTINGS,
  ChangeBillingSettingsAction,
  ChangeBillingSettingsSuccessAction,
  ChangeBillingSettingsFailureAction,
  GET_INTEGRATION_SETTINGS,
  GetIntegrationSettingsSuccessAction,
  GetIntegrationSettingsFailureAction,
  CHANGE_INTEGRATION_SETTINGS,
  CHANGE_INTEGRATION_SETTINGS_FAILURE,
  ChangeIntegrationSettingsAction,
  ChangeIntegrationSettingsSuccessAction,
  ChangeIntegrationSettingsFailureAction,
} from "./settings.actions";
import { SettingsService } from "./settings.service";
import { BillingSettingsService } from "../billing/settings/billing-settings.service";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import {
  AdvancedSettings,
  BillingSettings,
  CompanySettings,
  EmployeeSetting,
  PredefinedEmployeeSetting,
  IntegrationSettings,
} from "./settings.interface";
import { CompanyService } from "../company/company.service";
import { AdvancedSettingsService } from "../advanced-settings/advanced-settings.service";
import { IntegrationSettingsService } from "../integration-settings/integration-settings.service";

@Injectable()
export class SettingsEffects {
  getPredefinedEmployeeSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_PREDEFINED_EMPLOYEE_SETTINGS),
      switchMap(() => {
        return this.settingsService.getPredefinedEmployeeSettings().pipe(
          switchMap((response: PredefinedEmployeeSetting[]) => {
            return [new GetPredefinedEmployeeSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new GetPredefinedEmployeeSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  getEmployeeSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_EMPLOYEE_SETTINGS),
      switchMap(() => {
        return this.settingsService.getEmployeeSettings().pipe(
          switchMap((response: EmployeeSetting[]) => {
            return [new GetEmployeeSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new GetEmployeeSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeEmployeeSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_EMPLOYEE_SETTINGS),
      map((action: ChangeEmployeeSettingsAction) => action.payload),
      switchMap((payload: EmployeeSetting[]) => {
        return this.settingsService.changeEmployeeSettings(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant(
                "settings.employeeDataEditSuccess",
              ) as string,
            );
            return [
              new ChangeEmployeeSettingsSuccessAction(payload),
              new GetEmployeeSettingsAction(),
            ];
          }),
          catchError((error: unknown) =>
            of(new ChangeEmployeeSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeEmployeeSettingsFailure$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CHANGE_EMPLOYEE_SETTINGS_FAILURE),
        tap((action: ChangeEmployeeSettingsFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("CUSTOM_VARIABLES_LIMIT_REACHED")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "customVariable" },
            });
          } else {
            this.toastr.error(
              this.translate.instant(
                "settings.employeeDataEditError",
              ) as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  getCompanySettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_COMPANY_SETTINGS),
      switchMap(() => {
        return this.companyService.get().pipe(
          switchMap((response: CompanySettings) => {
            return [new GetCompanySettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new GetCompanySettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeCompanySettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_COMPANY_SETTINGS),
      map((action: ChangeCompanySettingsAction) => action.payload),
      switchMap((payload: CompanySettings) => {
        return this.companyService.save(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant(
                "settings.companyDataEditSuccess",
              ) as string,
            );
            return [new ChangeCompanySettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new ChangeCompanySettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  getAdvancedSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_ADVANCED_SETTINGS),
      switchMap(() => {
        return this.advancedSettingsService.get().pipe(
          switchMap((response: AdvancedSettings) => {
            return [new GetAdvancedSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new GetAdvancedSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeAdvancedSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_ADVANCED_SETTINGS),
      map((action: ChangeAdvancedSettingsAction) => action.payload),
      switchMap((payload: AdvancedSettings) => {
        return this.advancedSettingsService.update(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant(
                "settings.advancedSettingsEditSuccess",
              ) as string,
            );
            return [
              new ChangeAdvancedSettingsSuccessAction(response ?? payload),
            ];
          }),
          catchError((error: unknown) =>
            of(new ChangeAdvancedSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeAdvancedSettingsFailure$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CHANGE_ADVANCED_SETTINGS_FAILURE),
        tap((action: ChangeAdvancedSettingsFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("ANALYTICS_NOT_AVAILABLE")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "analytics" },
            });
          } else {
            this.toastr.error(
              this.translate.instant(
                "settings.advancedSettingsEditError",
              ) as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  getBillingSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_BILLING_SETTINGS),
      switchMap(() => {
        return this.billingSettingsService.get().pipe(
          switchMap((response: BillingSettings) => {
            return [new GetBillingSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new GetBillingSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeBillingSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_BILLING_SETTINGS),
      map((action: ChangeBillingSettingsAction) => action.payload),
      switchMap((payload: BillingSettings) => {
        return this.billingSettingsService.update(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("settings.billingEditSuccess") as string,
            );
            return [new ChangeBillingSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new ChangeBillingSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  getIntegrationSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(GET_INTEGRATION_SETTINGS),
      switchMap(() => {
        return this.integrationSettingsService.get().pipe(
          switchMap((response: IntegrationSettings) => [
            new GetIntegrationSettingsSuccessAction(response),
          ]),
          catchError((error: unknown) =>
            of(new GetIntegrationSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeIntegrationSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_INTEGRATION_SETTINGS),
      map((action: ChangeIntegrationSettingsAction) => action.payload),
      switchMap((payload: IntegrationSettings) => {
        return this.integrationSettingsService.update(payload).pipe(
          switchMap((response: IntegrationSettings) => {
            this.toastr.success(
              this.translate.instant(
                "settings.integrationSettingsEditSuccess",
              ) as string,
            );
            return [new ChangeIntegrationSettingsSuccessAction(response)];
          }),
          catchError((error: unknown) =>
            of(new ChangeIntegrationSettingsFailureAction(error)),
          ),
        );
      }),
    ),
  );

  changeIntegrationSettingsFailure$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CHANGE_INTEGRATION_SETTINGS_FAILURE),
        tap((action: ChangeIntegrationSettingsFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("DOMAIN_RULES_NOT_AVAILABLE")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "domainRules" },
            });
          } else if (
            errorMessage.includes("INTERNAL_SIGNATURE_NOT_AVAILABLE")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "internalSignature" },
            });
          } else {
            this.toastr.error(
              this.translate.instant(
                "settings.integrationSettingsEditError",
              ) as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private settingsService: SettingsService,
    private companyService: CompanyService,
    private advancedSettingsService: AdvancedSettingsService,
    private integrationSettingsService: IntegrationSettingsService,
    private billingSettingsService: BillingSettingsService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
